import { useState, useMemo, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { findActiveSection } from './navigationConfig.js';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useAuth } from '@aimsos/auth';
import { ChatProviderWrapper, BasicChatPanel } from '@aimsos/chat';
import ShellTopNav from './TopNav.jsx';
import ShellSideNav from './SideNav.jsx';
import { ShellChatRuntimeProvider } from './ShellChatRuntimeProvider.jsx';
import { ShellLayoutProvider, useShellLayout } from './ShellLayoutContext.jsx';
import { api } from '../services/apiClient.js';
import { useChatWebSocketFacade } from '../chat/useChatWebSocketFacade.js';
import { GripVertical } from 'lucide-react';

/**
 * Root layout for the Core Shell.
 * Manages sidebar expanded state and renders TopNav + SideNav + content.
 * Wraps the shell with `@aimsos/chat` providers for the assistant chat panel smoke test.
 *
 * Sidebar expanded state is stored in ShellLayoutContext and persisted to localStorage
 * so it survives page refreshes.
 */
const ShellLayoutInner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('core-shell');
  const { expanded, toggleExpanded } = useShellLayout();

  const sideNavItems = useMemo(() => {
    const active = findActiveSection(location.pathname);
    return (active?.items ?? []).map((item) => ({
      ...item,
      label: item.labelKey ? t(item.labelKey) : item.label,
      // Translate submenu children too (e.g. HTL Settings → Conditions / Teams & Queues / Destinations).
      ...(item.items
        ? {
            items: item.items.map((child) => ({
              ...child,
              label: child.labelKey ? t(child.labelKey) : child.label,
            })),
          }
        : {}),
    }));
  }, [location.pathname, t]);
  const [chatOpen, setChatOpen] = useState(false);
  /** `'split'` — beside main content; `'fullscreen'` — assistant uses the full content row. */
  const [chatLayout, setChatLayout] = useState('split');
  const shellMainChatGroupRef = useRef(null);

  const handleCloseChat = useCallback(() => {
    setChatOpen(false);
    setChatLayout('split');
  }, []);

  const handleOpenAssistant = useCallback(() => {
    setChatOpen(true);
    setChatLayout('split');
  }, []);

  const handleOpenInHome = useCallback(() => {
    navigate('/dashboard');
    setChatOpen(false);
    setChatLayout('split');
  }, [navigate]);
  const handleOpenTasks = useCallback(() => {
    navigate('/tasks');
  }, [navigate]);
  // useAuth() (not useAuth0() directly) so name/email/picture still display
  // correctly on a {slug}.{rootDomain} landed on via a cross-subdomain
  // handoff, where Auth0 SDK itself has no session (see profileCache.js).
  const { user } = useAuth();
  const { wsService } = useChatWebSocketFacade();
  const toast = useToast();

  const chatNotifications = useMemo(
    () => ({
      success: (opts) => toast.success?.(opts),
      error: (opts) => toast.error?.(opts),
    }),
    [toast],
  );

  const [userId] = useState(() => localStorage.getItem('user_uuid') || '');
  const [tenantId] = useState(() => localStorage.getItem('workspace_uuid') || '');

  const authStore = useMemo(
    () => ({
      userId,
      currentWorkspaceUuid: tenantId,
      userData: {
        first_name: user?.given_name,
        firstName: user?.given_name,
        full_name: user?.name,
        name: user?.name,
      },
      currentUserFirstName: user?.given_name,
    }),
    [user, userId, tenantId],
  );

  useEffect(() => {
    // topbarShellSlotClass: h-topbar (2.25rem/36px) on md+, max-md:h-[34px] on mobile
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => {
      document.documentElement.style.setProperty(
        '--modal-topbar-height',
        mq.matches ? '2.25rem' : '34px',
      );
    };
    update();
    mq.addEventListener('change', update);
    return () => {
      mq.removeEventListener('change', update);
      document.documentElement.style.removeProperty('--modal-topbar-height');
    };
  }, []);

  useEffect(() => {
    // Collapsed: collapsedWidth(54) + floating padding(16) = 70px
    // Expanded: 250px (Sidebar default when no expandedWidth prop is passed)
    const sidebarPx = sideNavItems.length === 0 ? 0 : expanded ? 250 : 70;
    document.documentElement.style.setProperty('--modal-sidebar-offset', `${sidebarPx}px`);
    return () => document.documentElement.style.removeProperty('--modal-sidebar-offset');
  }, [expanded, sideNavItems.length]);

  useLayoutEffect(() => {
    if (chatLayout === 'fullscreen') return;
    const group = shellMainChatGroupRef.current;
    if (!group) return;
    if (chatOpen) {
      group.setLayout([68, 32]);
    } else {
      group.setLayout([100, 0]);
    }
  }, [chatOpen, chatLayout]);

  const chatHostRuntimeValue = useMemo(
    () => ({
      apiClient: api,
      wsService,
      authStore,
      auth0User: user,
      notifications: chatNotifications,
    }),
    [wsService, authStore, user, chatNotifications],
  );

  return (
    <ChatProviderWrapper
      apiClient={api}
      wsService={wsService}
      userId={userId}
      tenantId={tenantId}
      authStore={authStore}
      auth0User={user}
      notifications={chatNotifications}
    >
      <ShellChatRuntimeProvider
        baseRuntime={chatHostRuntimeValue}
        setChatOpen={setChatOpen}
        setChatLayout={setChatLayout}
      >
        <div className="flex h-full flex-col">
        <ShellTopNav
          onMenuClick={toggleExpanded}
          onOpenAssistant={handleOpenAssistant}
        />
        <div className="flex flex-1 overflow-hidden">
          <ShellSideNav
            items={sideNavItems}
            expanded={expanded}
            onToggleExpand={toggleExpanded}
          />
          {chatOpen && chatLayout === 'fullscreen' ? (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              {/* Outlet must stay mounted so embedded routes keep their state. */}
              <div className="hidden" aria-hidden>
                <Outlet />
              </div>
              <BasicChatPanel
                layout="fullscreen"
                onLayoutChange={setChatLayout}
                onClose={handleCloseChat}
                onOpenInHome={handleOpenInHome}
                onOpenTasks={handleOpenTasks}
              />
            </div>
          ) : (
            <PanelGroup
              ref={shellMainChatGroupRef}
              direction="horizontal"
              id="core-shell-main-chat"
              className="flex min-h-0 min-w-0 flex-1"
            >
              <Panel id="shell-main" order={1} defaultSize={100} minSize={50}>
                <div
                  id="shell-main-content"
                  className="relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                >
                  <Outlet />
                </div>
              </Panel>
              <PanelResizeHandle
                disabled={!chatOpen}
                className={[
                  'group relative shrink-0 cursor-col-resize bg-gray-200 transition-colors',
                  'dark:bg-gray-700 hover:bg-blue-500 dark:hover:bg-blue-600',
                  'data-[resize-handle-active]:bg-blue-500 dark:data-[resize-handle-active]:bg-blue-600',
                  !chatOpen ? 'pointer-events-none w-0 min-w-0 overflow-hidden opacity-0' : 'w-1',
                ].join(' ')}
              >
                {chatOpen ? (
                  <div className="absolute inset-y-0 left-1/2 flex -translate-x-1/2 items-center justify-center">
                    <div
                      className={[
                        'rounded-full bg-gray-300 p-1 opacity-0 transition-opacity',
                        'dark:bg-gray-600 group-hover:opacity-100',
                        'group-hover:bg-blue-500 dark:group-hover:bg-blue-600',
                        'group-data-[resize-handle-active]:opacity-100',
                        'group-data-[resize-handle-active]:bg-blue-500 dark:group-data-[resize-handle-active]:bg-blue-600',
                      ].join(' ')}
                    >
                      <GripVertical className="size-3 text-white" aria-hidden />
                    </div>
                  </div>
                ) : null}
              </PanelResizeHandle>
              <Panel
                id="shell-assistant"
                order={2}
                defaultSize={chatOpen ? 32 : 0}
                minSize={chatOpen ? 20 : 0}
                maxSize={50}
                className="min-h-0 min-w-0"
              >
                {chatOpen ? (
                  <div className="flex h-full min-h-0 min-w-0 flex-col">
                    <BasicChatPanel
                      layout="split"
                      splitResizeSurface="host"
                      onLayoutChange={setChatLayout}
                      onClose={handleCloseChat}
                      onOpenInHome={handleOpenInHome}
                      onOpenTasks={handleOpenTasks}
                    />
                  </div>
                ) : null}
              </Panel>
            </PanelGroup>
          )}
        </div>
        </div>
      </ShellChatRuntimeProvider>
    </ChatProviderWrapper>
  );
};

const ShellLayout = () => (
  <ShellLayoutProvider>
    <ShellLayoutInner />
  </ShellLayoutProvider>
);

export default ShellLayout;
